import React, { ChangeEvent } from "react";
import { Prompt, RouteComponentProps } from "react-router";
import { V2 } from "../../Api";

import withTitle from "../withTitle";

interface IMetricsAssessmentTypeEditViewState {
    isLoading: boolean | string;
    isSaving: boolean | string;
    hasChanged: boolean;

    questions: any[];
}

class MetricsAssessmentTypeEditView extends React.Component<RouteComponentProps<any>, IMetricsAssessmentTypeEditViewState> {
    constructor(props: Readonly<RouteComponentProps<any>>) {
        super(props);

        const state: IMetricsAssessmentTypeEditViewState = {
            isLoading: true,
            isSaving: false,
            hasChanged: false,

            questions: []
        };

        this.state = state;

        this.renderNumberDropdown = this.renderNumberDropdown.bind(this);
        this.handleNumberChange = this.handleNumberChange.bind(this);
        this.saveChanges = this.saveChanges.bind(this);
        this.cancelChanges = this.cancelChanges.bind(this);
        this.addQuestion = this.addQuestion.bind(this);
    }

    public componentDidMount() {
        this.loadData();
    }

    public render() {
        return (
            <div className="viewer-root">
                <Prompt when={this.state.hasChanged} message="Your changes will not be saved if you continue." />
                <table className="table-vertical">
                    <thead>
                        <tr>
                            <th className="col-num">#</th>
                            <th>Type</th>
                            <th>Text</th>
                        </tr>
                    </thead>
                    <tbody>
                        {this.state.questions.map((a, i) => <tr key={a.id}>
                            <td>{this.renderNumberDropdown(a.id, i)}</td>
                            <td>
                                <select name="type" value={a.type} onChange={e => this.handleInputChanged(e, a)}>
                                    <option value="">Select type</option>
                                    <optgroup label="Text">
                                        <option value="text">Single-line text</option>
                                        <option value="text.multiline">Multi-line text</option>
                                    </optgroup>
                                    <optgroup label="Yes/No">
                                        <option value="yesno">Yes/No</option>
                                        <option value="yesno.na">Yes/No + N/A</option>
                                    </optgroup>
                                    <optgroup label="Scales">
                                        <option value="scale.m2-2.change">Improvement scale</option>
                                        <option value="scale.1-5.expectation">Expectation scale</option>
                                        <option value="scale.1-5.expectation.na">Expectation scale + N/A</option>
                                        <option value="scale.1-5.expectation2">"We need to talk" scale</option>
                                        <option value="scale.1-5.expectation2.na">"We need to talk" + N/A</option>
                                        <option value="scale.1-5.agree">Agreement scale</option>
                                    </optgroup>
                                </select>
                            </td>
                            <td>
                                <textarea name="text" value={a.text} onChange={e => this.handleInputChanged(e, a)} />
                            </td>
                        </tr>)}
                        <tr>
                            <td colSpan={3}>
                                <button type="button" className="button text-with-icon" onClick={this.addQuestion}><i className="icon-plus" /><span>Add Question</span></button>
                            </td>
                        </tr>
                    </tbody>
                </table>
                {this.state.isSaving === true
                    ? <div className="spinner lds-dual-ring" />
                    : typeof this.state.isSaving === "string"
                        ? <div className="error-text">{this.state.isSaving}</div>
                        : null}
                &nbsp;
                <div className="action-panel-dialog">
                    <span className="spring" />
                    <button type="button" className="button text-with-icon" onClick={this.saveChanges}><i className="icon-check" /><span>Save</span></button>
                    <button type="button" className="button text-with-icon" onClick={this.cancelChanges}><i className="icon-times" /><span>Cancel</span></button>
                </div>
            </div>
        );
    }

    private async loadData() {
        try {
            const assessment = await V2.get<any>(`metrics/assessments/types/${this.props.match.params.assessment_type_id}`);

            this.setState({
                isLoading: false,
                questions: assessment.questions || []
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not load assessment questions"
            });
        }
    }

    private handleInputChanged(e: ChangeEvent<HTMLTextAreaElement | HTMLSelectElement>, question: any) {
        const property = e.currentTarget.name;
        const value = e.currentTarget.value;
        const questions = this.state.questions;

        question[property] = value;

        console.log(question);

        this.setState({
            hasChanged: true,
            questions
        });
    }

    private renderNumberDropdown(id: string, selected: number) {
        return <select value={selected} onChange={e => this.handleNumberChange(e, id)}>
            {this.state.questions.map((a, i) => <option key={i} value={i}>{i + 1}</option>)}
            <option value={-1}>Delete</option>
        </select>;
    }

    private handleNumberChange(e: ChangeEvent<HTMLSelectElement>, id: string) {
        const questions = this.state.questions;
        const index = questions.findIndex(a => a.id === id);
        let newIndex = +e.currentTarget.value;
        if (index < 0 || isNaN(newIndex)) {
            return;
        }

        // Handle case where the item is moving downward
        /*if (newIndex > index) {
            newIndex--;
        }*/

        // Remove question from its previous place and optionally splice it into the new place
        const [question] = questions.splice(index, 1);
        if (newIndex >= 0) {
            // Add question in new place
            questions.splice(newIndex, 0, question);
        }

        // Renumber items
        for (let i = 0; i < questions.length; i++) {
            questions[i].position = i;
        }

        this.setState({
            hasChanged: true,
            questions
        });
    }

    private async saveChanges() {
        try {
            this.setState({
                isSaving: true
            });

            const data = this.state.questions;

            // Renumber items to match what's on screen
            for (let i = 0; i < data.length; i++) {
                data[i].position = i;
                if (!data[i].type || !data[i].text) {
                    this.setState({
                        isSaving: "Please make sure all added questions have types and texts set"
                    });
                    return;
                }
            }

            await V2.put(`metrics/assessments/types/${this.props.match.params.assessment_type_id}/questions`, data);

            this.setState({
                hasChanged: false,
            }, () => this.props.history.goBack());
        }
        catch (e) {
            this.setState({
                isSaving: e.message || "Could not save changes"
            });
        }
    }

    private cancelChanges() {
        this.props.history.goBack();
    }

    private addQuestion() {
        const questions = this.state.questions;

        questions.push({
            id: Date.now().toString(),
            type: "",
            text: "",
        });

        this.setState({
            questions
        });
    }
}

export default withTitle("Employee Assessment Type Questions", MetricsAssessmentTypeEditView);
